/**
 * AuthContext — single source of truth for the signed-in user.
 *
 * Surface:
 *   - user, loading
 *   - login(email, password)
 *   - logout()
 *   - refreshUser()
 *   - isRole(role | role[])
 *
 * Mounts an "unauthorized" handler with the api-client so a 401 after refresh
 * failure cleanly logs the user out.
 */
import {
  createContext, useContext, useState, useEffect, useCallback, useMemo, useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { authApi, tokens, setUnauthorizedHandler, setCompanyDisabledHandler } from "@/lib/api-client";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { User, Role } from "@/types/api";

/** Routes where a "session expired" toast would be misleading because
 *  there's no session to expire — the visitor is on a public surface
 *  (sign-in, password recovery, activation links). Matched as exact path
 *  or prefix-with-slash so query strings / sub-paths don't slip through.
 *
 *  Keep this list aligned with the public routes registered in
 *  src/routes/index.tsx. */
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/activate-admin",
  "/support",
  "/privacy",
  "/eula",
  "/terms",
];

function isOnPublicPath(): boolean {
  const path = typeof window === "undefined" ? "" : window.location.pathname;
  return PUBLIC_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Last-known user, persisted to localStorage (profile only — no tokens). On an
 * OFFLINE page reload the access token is gone and /api/auth/refresh can't be
 * reached, so without this the bootstrap would log the user out. We instead
 * restore this cached identity and let the app run offline (Dexie serves data);
 * a real online refresh re-validates on reconnect. Cleared on explicit logout
 * or a genuine 401.
 */
const LAST_USER_KEY = "icheck.lastUser";
function persistUser(u: User): void {
  try { localStorage.setItem(LAST_USER_KEY, JSON.stringify(u)); } catch { /* storage disabled */ }
}
function loadPersistedUser(): User | null {
  try { const s = localStorage.getItem(LAST_USER_KEY); return s ? (JSON.parse(s) as User) : null; } catch { return null; }
}
function clearPersistedUser(): void {
  try { localStorage.removeItem(LAST_USER_KEY); } catch { /* storage disabled */ }
}

interface AuthContextShape {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, mfaToken?: string) => Promise<User | { mfaRequired: true }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  setUser: (u: User | null) => void;
  isRole: (role: Role | Role[]) => boolean;
}

const AuthContext = createContext<AuthContextShape | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const broadcast = useRef<BroadcastChannel | null>(null);
  const queryClient = useQueryClient();
  // Session-expired modal state. When the refresh cookie is missing /
  // expired / rejected, the onUnauthorized handler sets this true; the
  // <Dialog> below opens, the user must click "Sign in again" to dismiss.
  // Idempotent: concurrent 401s all setOpen(true) — React no-ops the
  // second-through-Nth, so no extra dedup ref is needed for the modal.
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  // Company-disabled modal: set to the backend's message when a request is
  // rejected with COMPANY_DISABLED (the platform admin turned off this tenant).
  const [companyDisabledMsg, setCompanyDisabledMsg] = useState<string | null>(null);

  // Refs read by the (closure-captured) onUnauthorized handler. The
  // handler is registered once in the bootstrap effect with empty deps,
  // so it must reach the LATEST user state via a ref rather than the
  // captured value.
  const userRef = useRef<User | null>(null);
  // queryClient changes identity between mounts; ref keeps the handler
  // (registered once below) talking to the current instance.
  const queryClientRef = useRef(queryClient);

  // Keep the ref in sync with state. This fires after each render, but
  // critical paths (logout, button-click, onUnauthorized) also update the
  // ref SYNCHRONOUSLY so there's no gap where a concurrent 401 could see
  // stale "still signed in" and re-open the expired-session modal.
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { queryClientRef.current = queryClient; }, [queryClient]);

  const clearUserScopedCache = useCallback(() => {
    queryClient.cancelQueries();
    queryClient.clear();
  }, [queryClient]);

  /* -------- one-time bootstrap -------- */
  useEffect(() => {
    let alive = true;

    setUnauthorizedHandler(() => {
      // Security: drop tokens immediately so no further API calls can
      // succeed. But DON'T clear React user state yet — we want the
      // user to stay on their current page and see the modal overlay
      // before being redirected to /login. The user state (and query
      // cache) are cleared when they click "Sign in again".
      tokens.clear();

      const wasSignedIn = userRef.current !== null;

      // On a public page (login, activation, etc.) or during bootstrap
      // (no active session), just silently wipe and move on — no modal.
      // In that case we DO clear state immediately since there's nothing
      // to show the user.
      if (!wasSignedIn || isOnPublicPath()) {
        userRef.current = null;
        setUserState(null);
        clearPersistedUser();
        queryClientRef.current.clear();
        return;
      }

      // Show the session-expired modal ON TOP of the current page.
      // Dedup is intrinsic: setOpen(true) when already true is a no-op,
      // so concurrent 401s collapse to one modal naturally.
      setSessionExpiredOpen(true);
    });

    setCompanyDisabledHandler((msg) => {
      // The tenant was disabled mid-session. Drop tokens so nothing else can
      // succeed (same as onUnauthorized), then show a blocking modal with the
      // backend's message; state is cleared when the user acknowledges. On a
      // public path / no session, just wipe silently.
      tokens.clear();
      const wasSignedIn = userRef.current !== null;
      if (!wasSignedIn || isOnPublicPath()) {
        userRef.current = null;
        setUserState(null);
        clearPersistedUser();
        queryClientRef.current.clear();
        return;
      }
      setCompanyDisabledMsg(msg);
    });

    (async () => {
      // Bootstrap after the cookie migration:
      //
      // The access token now lives only in JS memory (api-client's
      // `tokens.access`), so it's null on every fresh page load. The
      // refresh token, however, is an httpOnly cookie — invisible to
      // JS but auto-attached to /api/auth requests by the browser.
      //
      // So the bootstrap dance is: blindly POST /api/auth/refresh. If
      // the cookie is present and valid we'll get a new access token
      // back, can then call /auth/me, and the user is signed in. If the
      // cookie is missing or expired, refresh returns 401 → we treat
      // this as "not signed in" and silently land on the login surface.
      //
      // No optimisation possible here: we can't peek at the httpOnly
      // cookie to short-circuit. The cost is one extra request per
      // page load while logged out — acceptable for the security gain.
      // Boot must NEVER hang on the auth handshake. On native an unreachable
      // backend (stale LAN IP, server down, no connectivity) leaves the raw
      // fetch in /api/auth/refresh pending indefinitely — so without a bound the
      // finally below never runs, `loading` stays true, and the app is stuck on
      // the launch spinner forever. Race every boot request against a timeout:
      // on expiry we treat it as "couldn't reach the server" and fall through to
      // the optimistic cached-user / login path, exactly like an offline reload.
      const BOOT_AUTH_TIMEOUT_MS = 6000;
      const withTimeout = <T,>(p: Promise<T>, fallback: T): Promise<T> =>
        Promise.race([
          p,
          new Promise<T>((resolve) => { setTimeout(() => resolve(fallback), BOOT_AUTH_TIMEOUT_MS); }),
        ]);
      try {
        // Native: restore the refresh token persisted in Preferences so the
        // session survives an app restart (the cross-origin cookie doesn't).
        // No-op on web (the cookie carries it).
        await tokens.load();
        const ok = await withTimeout(authApi.refreshAccessFromCookie(), false);
        if (!alive) return;
        if (!ok) {
          // Refresh failed — could be offline, server unreachable, OR a
          // genuinely expired session. We can't tell apart from here, so if we
          // have a remembered user we restore it OPTIMISTICALLY (keeps an
          // offline reload signed in). A genuinely-expired session self-corrects:
          // the first authenticated request returns 401 → onUnauthorized →
          // session-expired modal → logout. Restoring covers both the
          // DevTools-Offline case and the "server stopped, Wi-Fi up" case.
          const cached = loadPersistedUser();
          if (cached) setUserState(cached);
          return;
        }
        const me = await withTimeout(authApi.me().catch(() => null), null);
        if (!alive) return;
        if (!me) {
          // Refresh succeeded but /auth/me stalled — keep any remembered user
          // rather than blocking; a real fetch reconciles on the next request.
          const cached = loadPersistedUser();
          if (cached) setUserState(cached);
          return;
        }
        setUserState(me);
        persistUser(me);
      } catch {
        // Refresh/me threw (network) — restore the remembered user if any.
        if (!alive) return;
        const cached = loadPersistedUser();
        if (cached) setUserState(cached);
        else tokens.clear();
      } finally {
        if (alive) setLoading(false);
      }
    })();

    // Cross-tab sync: when one tab logs out, others follow.
    // Both branches also drop the cache so the receiving tab can't
    // serve stale per-user data after the auth transition.
    if (typeof BroadcastChannel !== "undefined") {
      broadcast.current = new BroadcastChannel("icheck:auth");
      broadcast.current.onmessage = (ev) => {
        if (ev.data?.type === "logout") {
          userRef.current = null;
          setUserState(null);
          clearPersistedUser();
          queryClientRef.current.clear();
        }
        if (ev.data?.type === "login") void refreshUserFn();
      };
    }

    return () => {
      alive = false;
      setUnauthorizedHandler(null);
      setCompanyDisabledHandler(null);
      broadcast.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUserFn = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUserState(me);
      persistUser(me);
      return me;
    } catch {
      setUserState(null);
      return null;
    }
  }, []);

  const login = useCallback(async (email: string, password: string, mfaToken?: string) => {
    const result = await authApi.login(email, password, undefined, mfaToken);
    if ("mfaRequired" in result && result.mfaRequired) {
      return { mfaRequired: true as const };
    }
    // The early-return above guarantees `result` is AuthResult here, but
    // TS's narrowing on the compound `in` + truthy check doesn't track
    // that through the union. A simple assertion is safe and avoids a
    // structural refactor of the login return type.
    const auth = result as import("@/types/api").AuthResult;
    setUserState(auth.user);
    persistUser(auth.user);
    setSessionExpiredOpen(false);
    broadcast.current?.postMessage({ type: "login" });
    return auth.user;
  }, []);

  const logout = useCallback(async () => {
    // Hit the backend so the refresh token is invalidated server-side
    // (otherwise it stays usable until its 30-day TTL). Swallow errors
    // — even an offline / failed call must still wipe local auth.
    try { await authApi.logout(); } catch { /* swallow */ }
    // tokens.clear() runs inside authApi.logout, but we re-call it
    // here in case the request threw before reaching the finally block
    // (e.g. the user's network dropped mid-flight).
    tokens.clear();
    clearPersistedUser();
    userRef.current = null;
    setUserState(null);
    queryClientRef.current.clear();
    broadcast.current?.postMessage({ type: "logout" });
  }, [clearUserScopedCache]);

  const isRole = useCallback(
    (role: Role | Role[]) => {
      if (!user) return false;
      const roles = Array.isArray(role) ? role : [role];
      return roles.includes(user.role);
    },
    [user],
  );

  const value = useMemo<AuthContextShape>(
    () => ({ user, loading, login, logout, refreshUser: refreshUserFn, setUser: setUserState, isRole }),
    [user, loading, login, logout, refreshUserFn, isRole],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Session-expired notification — replaces the previous sonner
       *  toast. Non-dismissible by Escape or outside click so the user
       *  has to acknowledge before the modal goes away. Tokens, React
       *  state, and the query cache were already cleared the moment the
       *  401 surfaced; <ProtectedRoute> has already auto-redirected to
       *  /login by the time this modal opens, so the "Sign in again"
       *  button just dismisses the alert — the user is already on the
       *  login page. */}
      <Dialog
        open={sessionExpiredOpen}
        onOpenChange={(open) => {
          // Only allow the dialog to be closed via the button below.
          // Ignoring "close" requests from Escape / overlay clicks
          // forces explicit acknowledgement.
          if (open) setSessionExpiredOpen(true);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="max-w-md"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-danger/10">
              <AlertCircle className="h-5 w-5 text-danger" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <DialogTitle className="text-base">Session expired</DialogTitle>
              <DialogDescription className="text-sm text-fg-muted">
                Your session has expired. Please log in again to continue.
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button
              autoFocus
              className="w-full sm:w-auto"
              onClick={() => {
                // Sync the ref FIRST so any concurrent 401 (from a
                // stale background query firing during the transition)
                // sees "not signed in" and skips the modal — without
                // this, the useEffect-based ref sync lags behind by one
                // render frame, and the modal can re-open on /login.
                userRef.current = null;
                setUserState(null);
                queryClientRef.current.clear();
                setSessionExpiredOpen(false);
              }}
            >
              Sign in again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company disabled: the platform admin turned off this tenant mid-session.
          Blocking modal (button-only dismiss) showing the backend's message;
          the user lands back on /login once state clears. */}
      <Dialog open={companyDisabledMsg !== null} onOpenChange={() => { /* button-only dismiss */ }}>
        <DialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="max-w-md"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-danger/10">
              <AlertCircle className="h-5 w-5 text-danger" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <DialogTitle className="text-base">Account disabled</DialogTitle>
              <DialogDescription className="text-sm text-fg-muted">
                {companyDisabledMsg}
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button
              autoFocus
              className="w-full sm:w-auto"
              onClick={() => {
                userRef.current = null;
                setUserState(null);
                clearPersistedUser();
                queryClientRef.current.clear();
                setCompanyDisabledMsg(null);
              }}
            >
              Sign in again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
